import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Inbox, Plus, Funnel, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, LoadingState, PageHeader, ui } from "../../../components/ui";
import {
  WithdrawDialog,
  RevokeLeaveModal,
  LeaveActionToast,
  ManagerRemarksCell,
  EmployeeLeaveFiltersModal,
  EmployeeLeaveMobileList,
  LeaveDetailsModal,
  LeaveStatusBadge,
  TablePagination,
  countEmployeeActiveFilters,
  getDefaultEmployeeLeaveFilters } from
"../components";
import { getEmployeeLeaveRowAction } from "../components/employeeLeaveRowAction";
import { leaveService } from "../../../services/leaveService";
import sessionDB from "../../../utils/sessionDB";
import {
  getLeaveApiErrorMessage,
  buildSuccessToast } from
"../../../lib/leaveApiMessages";
import { mapLeaveRecords } from "../../../lib/leaveRecords";

const PAGE_SIZE = 10;

export function LeaveHistoryPage() {
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawId, setWithdrawId] = useState(null);
  const [revokeRequest, setRevokeRequest] = useState(null);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  const [filters, setFilters] = useState(getDefaultEmployeeLeaveFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const requestVersion = useRef(0);
  const [selectedLeave, setSelectedLeave] = useState(null);

  useEffect(() => {
    leaveService
    .getLeaveTypes()
    .then(setLeaveTypes)
    .catch((err) => {
      console.error("Failed to load leave types", err);
    });
  }, []);

  const loadLeaveHistory = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const sessionEmpId = Number(sessionDB.getEmpId());
      const optionalEmpId =
      Number.isFinite(sessionEmpId) && sessionEmpId > 0 ? sessionEmpId : undefined;
      const result = await leaveService.getEmployeeLeaveTablePage(optionalEmpId, { page: page - 1, size: PAGE_SIZE, filterLocally: countEmployeeActiveFilters(filters) > 0 });
      if (version !== requestVersion.current) return;
      setPagination(result);
      const records = result.content;
      setLeaveHistory(mapLeaveRecords(records, leaveTypes));
    } catch (err) {
      if (version !== requestVersion.current) return;
      setPagination(null);
      console.error("Failed to load employee requests", err);
      setLeaveHistory([]);
      const message = getLeaveApiErrorMessage(err);
      if (message) {
        setToast({ message, type: "error" });
      }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [leaveTypes, page, filters]);

  useEffect(() => {
    loadLeaveHistory();
  }, [loadLeaveHistory]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const visibleRows = useMemo(() => {
    let out = [...leaveHistory];
    const { leaveType, status, dateRange } = filters;

    if (leaveType !== "all") {
      out = out.filter((row) => String(row.leaveTypeId) === String(leaveType));
    }

    if (status !== "all") {
      out = out.filter((row) => row.status?.toLowerCase() === status.toLowerCase());
    }

    if (dateRange?.from) {
      out = out.filter((row) => {
        const rowDate = row.fromDate ? new Date(row.fromDate) : new Date(row.from);
        return rowDate >= dateRange.from;
      });
    }

    if (dateRange?.to) {
      out = out.filter((row) => {
        const rowDate = row.toDate ? new Date(row.toDate) : new Date(row.to ?? row.from);
        return rowDate <= dateRange.to;
      });
    }

    return out;
  }, [leaveHistory, filters]);

  const totalPages = Math.max(1, pagination?.serverPaged ? pagination.totalPages : Math.ceil(visibleRows.length / PAGE_SIZE));

  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [page, totalPages, loading]);

  const pagedRows = useMemo(() => {
    if (pagination?.serverPaged) return visibleRows;
    const start = (page - 1) * PAGE_SIZE;
    return visibleRows.slice(start, start + PAGE_SIZE);
  }, [visibleRows, page, pagination]);

  const activeFilterCount = countEmployeeActiveFilters(filters);

  const handleResetFilters = () => {
    setFilters(getDefaultEmployeeLeaveFilters());
    setPage(1);
  };

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const confirmWithdraw = async () => {
    if (!withdrawId || actionLoading) return;

    setActionLoading(true);
    setWithdrawError("");

    try {
      const response = await leaveService.withdrawLeaveRequest(Number(withdrawId));
      setWithdrawId(null);
      const successToast = buildSuccessToast(response, { includeRemarks: false });
      if (successToast) setToast(successToast);
      await loadLeaveHistory();
    } catch (err) {
      console.error("Failed to withdraw request", err);
      const message = getLeaveApiErrorMessage(err);
      if (message) {
        setWithdrawError(message);
        setToast({ message, type: "error" });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeConfirm = async () => {
























    // Revoke is temporarily disabled — see RevokeLeaveModal "feature coming soon" UI.
    /*
    const handleRevokeConfirm = async (revocationReason, checkedSessions) => {
      if (!revokeRequest || actionLoading) return;
       setActionLoading(true);
       try {
        const response = await leaveService.revokeLeaveRequest(Number(revokeRequest.id), {
          revocationReason,
          leavesDetail: sessionsToLeavesDetail(checkedSessions),
        });
        setRevokeRequest(null);
        const successToast = buildSuccessToast(response, { includeRemarks: false });
        if (successToast) setToast(successToast);
        await loadLeaveHistory();
      } catch (err) {
        console.error("Failed to revoke leave request", err);
        throw err;
      } finally {
        setActionLoading(false);
      }
    };
    */};const openWithdrawDialog = (id) => {if (actionLoading) return;setWithdrawError("");setWithdrawId(id);};const openRevokeDialog = (request) => {if (actionLoading) return;setRevokeRequest(request);};return <AppLayout overlay={<>
          <EmployeeLeaveFiltersModal open={filterModalOpen} onOpenChange={setFilterModalOpen} appliedFilters={filters} onApply={handleApplyFilters} leaveTypes={leaveTypes} />
      

          <LeaveDetailsModal
        open={!!selectedLeave}
        onOpenChange={(open) => {
          if (!open && !actionLoading) {
            setSelectedLeave(null);
          }
        }}
        leave={selectedLeave}
        onWithdraw={(leave) => {
          setSelectedLeave(null);
          openWithdrawDialog(leave.id);
        }}
        onRevoke={(leave) => {
          setSelectedLeave(null);
          openRevokeDialog(leave);
        }}
        actionLoading={actionLoading} />
      

          <WithdrawDialog
        open={!!withdrawId}
        onOpenChange={(open) => {
          if (!open && !actionLoading) {
            setWithdrawId(null);
            setWithdrawError("");
          }
        }}
        actionType="withdraw"
        loading={actionLoading}
        error={withdrawError}
        onConfirm={confirmWithdraw} />
      

          <RevokeLeaveModal
        open={!!revokeRequest}
        onOpenChange={(open) => {
          if (!open && !actionLoading) {
            setRevokeRequest(null);
          }
        }}
        leaveRequest={revokeRequest}
        onConfirm={handleRevokeConfirm} />
      

          <LeaveActionToast toast={toast} />
        </>
    }>
    
          <PageHeader back={<PageBackLink to="/hrms" />}
      title={i18n.t("leave.leaveHistory")}
      action={
      <Link to="/hrms/apply-leave" className={ui.primaryBtn}>
                <Plus className="h-4 w-4" />
                <Trans i18nKey="formActions.applyLeave" />
              </Link>
      } />
    

          <ContentCard>
            <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex items-center justify-end gap-1.5 flex-nowrap">
              <button
          type="button"
          onClick={() => setFilterModalOpen(true)}
          aria-label={i18n.t("formActions.filterLeaveRequests2")}
          className={ui.filterBtn}>
          
                <Funnel className="h-4 w-4 shrink-0" />
                <span className="hidden min-[400px]:inline"><Trans i18nKey="formActions.filter" /></span>
                {activeFilterCount > 0 &&
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
          }
              </button>
              <button
          type="button"
          onClick={handleResetFilters}
          disabled={activeFilterCount === 0}
          aria-label={i18n.t("formActions.resetFilters2")}
          className={`${ui.filterBtn} disabled:opacity-50 disabled:cursor-not-allowed`}>
          
                <RefreshCw className="h-4 w-4 shrink-0" />
                <span className="hidden min-[400px]:inline"><Trans i18nKey="formActions.reset" /></span>
              </button>
            </div>

            {loading ?
      <LoadingState message="Loading your leave history..." /> :
      visibleRows.length === 0 ?
      <EmptyState
        icon={Inbox}
        title={i18n.t("leave.noLeaveRequestsFound")}
        message="Try adjusting filters or apply for a new leave." /> :


      <>
                <EmployeeLeaveMobileList
          rows={pagedRows}
          onSelect={setSelectedLeave} />
        
                <HistoryTableBody
          rows={pagedRows}
          actionLoading={actionLoading}
          onSelect={setSelectedLeave}
          onWithdraw={openWithdrawDialog}
          onRevoke={openRevokeDialog} />
        
                <TablePagination
          page={pagination?.serverPaged ? pagination.pageNumber + 1 : page}
          totalPages={totalPages}
          totalItems={pagination?.serverPaged ? pagination.totalElements : visibleRows.length}
          pageSize={pagination?.serverPaged ? pagination.pageSize : PAGE_SIZE}
          disabled={loading}
          first={pagination?.serverPaged ? pagination.first : undefined}
          last={pagination?.serverPaged ? pagination.last : undefined}
          onPageChange={setPage} />
        
              </>
      }
          </ContentCard>
    </AppLayout>;

}

function HistoryTableBody({ rows, actionLoading, onSelect, onWithdraw, onRevoke }) {
  return (
    <div className="hidden md:block w-full overflow-x-auto">
      <table className="w-full text-sm min-w-[950px]">
        <thead>
          <tr className={ui.tableHead}>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveId" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveType" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leavePeriod" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="dateTime.days" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.appliedOn" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="status.status" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="common.managerRemarks" /></th>
            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap"><Trans i18nKey="common.action" /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-(--color-text-border)/35">
          {rows.map((r) =>
          <tr
            key={r.id}
            onClick={() => onSelect?.(r)}
            className="hover:bg-workspace/20 transition-colors cursor-pointer">
            
              <td className="px-4 py-3.5 font-semibold text-primary whitespace-nowrap">#{r.leaveId ?? r.id}</td>
              <td className="px-4 py-3.5 font-semibold text-foreground whitespace-nowrap">{r.type}</td>
              <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">{r.dateRange}</td>
              <td className="px-4 py-3.5 whitespace-nowrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-workspace/60 text-primary text-xs font-semibold">
                  {r.days} {r.days === 1 ? "Day" : "Days"}
                </span>
              </td>
              <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">{r.appliedOn}</td>
              <td className="px-4 py-3.5 whitespace-nowrap"><LeaveStatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 per-file-hint max-w-[240px]">
                <ManagerRemarksCell remarks={r.actionByRemarks} />
              </td>
              <td className="px-4 py-3.5 text-right whitespace-nowrap">
                {getEmployeeLeaveRowAction(r.status) === "withdraw" ?
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onWithdraw(r.id);
                }}
                disabled={actionLoading}
                className="inline-flex items-center justify-center h-8 px-3 rounded-lg border border-error/30 text-error hover:bg-error hover:text-white text-xs font-semibold transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    <Trans i18nKey="formActions.withdraw" />
                  
              </button> :
              getEmployeeLeaveRowAction(r.status) === "revoke" ?
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRevoke(r);
                }}
                disabled={actionLoading}
                className="inline-flex items-center justify-center h-8 px-3 rounded-lg border border-warning/40 text-warning hover:bg-warning hover:text-white text-xs font-semibold transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    <Trans i18nKey="formActions.revoke" />
                  
              </button> :

              <span className="per-file-hint">—</span>
              }
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>);

}
